import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { HeroSearchButton } from "./HeroSearchButton";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { colorSettingDetection, renderHeaderConditionally, themeTextureBackgroundImage } from "../../utils/functions";
import { formatFocusArea, focusAreaToCssPosition } from "../../utils/formatFocusArea";
import { HERO_SOURCES, heroSrc, IMAGE_QUALITY_DESKTOP, IMAGE_QUALITY_MOBILE, IMAGE_QUALITY_TABLET } from "@/lib/heroImageUtils";
import { heroLeadHeaderClasses, layoutHeaderBigClasses, layoutHeaderClasses, layoutSubheaderClasses } from "@/components/tailwindHelpers";
import type { HeroHeaderSize } from "@/lib/heroHeaderSize";
import Image from "next/image";
import { Breadcrumbs } from "@/components/terra/component/Breadcrumbs/Breadcrumbs";
import type { BreadcrumbCrumb } from "@/lib/breadcrumbs";
type ImageProps = {
  title: string;
  height: number;
  width: number;
  x: number;
  y: number;
  url: string;
  altText?: string;
  focusArea?: string;
};
export interface HeroLeadPanelProps {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  headerClassName?: string;
  subheader?: string;
  description?: string;
  backgroundImage?: ImageProps;
  presetTheme?: "t2-theme-one" | "t2-theme-two" | "t2-theme-three" | "t2-theme-four" | "t2-theme-five" | "t2-theme-six" | "t2-theme-seven";
  buttons?: TerraLink[];
  isHeaderBig?: boolean;
  /** Overrides isHeaderBig when set (see MXP-2076). */
  headerSize?: HeroHeaderSize;
  isWrapped?: boolean;
  isSearchEnabled?: boolean;
  searchPlaceholderText?: string;
  searchFilter?: string;
  searchIndex?: string;
  overlayOption?: "dark" | "light" | "none";
  heroStyle?: "lead" | "simple";
  componentImagePriority?: "high" | "auto" | "low";
  isLCP?: boolean;
  videoPresent?: boolean;
  breadcrumbs?: BreadcrumbCrumb[];
  breadcrumbDarkMode?: boolean;
}
function HeroBackgroundImage(t0) {
  const $ = _c(40);
  const {
    image,
    priority,
    isLCP
  } = t0;
  let alt;
  let desktop;
  let objectPos;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== image.altText || $[1] !== image.focusArea || $[2] !== image.title || $[3] !== image.url) {
    const focus = formatFocusArea(image.focusArea || "Center");
    let t9;
    if ($[15] !== image.focusArea) {
      t9 = focusAreaToCssPosition(image.focusArea);
      $[15] = image.focusArea;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    objectPos = t9;
    const base = image.url;
    const {
      mobile,
      tablet,
      desktop: t10,
      desktop2x
    } = HERO_SOURCES;
    desktop = t10;
    alt = image.altText || image.title || "";
    t2 = "absolute inset-0 z-[1] block w-full h-full";
    t3 = <source type="image/avif" media="(max-width: 639px)" srcSet={heroSrc(base, mobile.w, mobile.h, focus, "avif", IMAGE_QUALITY_MOBILE)} />;
    t4 = <source type="image/webp" media="(max-width: 639px)" srcSet={heroSrc(base, mobile.w, mobile.h, focus, "webp", IMAGE_QUALITY_MOBILE)} />;
    t5 = <source type="image/avif" media="(max-width: 1023px)" srcSet={heroSrc(base, tablet.w, tablet.h, focus, "avif", IMAGE_QUALITY_TABLET)} />;
    t6 = <source type="image/webp" media="(max-width: 1023px)" srcSet={heroSrc(base, tablet.w, tablet.h, focus, "webp", IMAGE_QUALITY_TABLET)} />;
    const t11 = `${heroSrc(base, desktop.w, desktop.h, focus, "avif", 85)} 1x, ${heroSrc(base, desktop2x.w, desktop2x.h, focus, "avif", 70)} 2x`;
    if ($[17] !== t11) {
      t7 = <source type="image/avif" srcSet={t11} />;
      $[17] = t11;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    const t12 = `${heroSrc(base, desktop.w, desktop.h, focus, "webp", 85)} 1x, ${heroSrc(base, desktop2x.w, desktop2x.h, focus, "webp", 70)} 2x`;
    if ($[19] !== t12) {
      t8 = <source type="image/webp" srcSet={t12} />;
      $[19] = t12;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    t1 = heroSrc(base, desktop.w, desktop.h, focus, "webp", 85);
    $[0] = image.altText;
    $[1] = image.focusArea;
    $[2] = image.title;
    $[3] = image.url;
    $[4] = alt;
    $[5] = desktop;
    $[6] = objectPos;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    alt = $[4];
    desktop = $[5];
    objectPos = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[21] !== objectPos) {
    t9 = {
      objectPosition: objectPos
    };
    $[21] = objectPos;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = isLCP ? "eager" : "lazy";
  const t11 = isLCP ? "high" : priority;
  let t12;
  if ($[23] !== alt || $[24] !== desktop.h || $[25] !== desktop.w || $[26] !== t1 || $[27] !== t10 || $[28] !== t11 || $[29] !== t9) {
    t12 = <img src={t1} alt={alt} width={desktop.w} height={desktop.h} className="w-full h-full object-cover" style={t9} loading={t10} fetchPriority={t11} decoding="async" />;
    $[23] = alt;
    $[24] = desktop.h;
    $[25] = desktop.w;
    $[26] = t1;
    $[27] = t10;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t13 = <picture className={t2}>{t3}{t4}{t5}{t6}{t7}{t8}{t12}</picture>;
    $[31] = t12;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
}
const LEAD_COLUMN_TW = ["basis-full max-w-full px-2.5 ml-0", "min-[960px]:basis-1/2 min-[960px]:max-w-[50%]"
// "min-[1231px]:basis-[41.666%] min-[1231px]:max-w-[41.666%]",
].join(" ");
const LEAD_WIDE_COLUMN_TW = ["basis-full max-w-full px-2.5 ml-0", "min-[960px]:basis-2/3 min-[960px]:max-w-[66.666%]"].join(" ");
const SIMPLE_COLUMN_TW = ["basis-full max-w-full px-2.5 ml-0", "min-lg:basis-[66.666%] min-lg:max-w-[66.666%]", "relative z-[3] w-full"].join(" ");
const SIMPLE_HEADER_TW = ["text-gray-m9 text-[20px] lg:text-[76px] font-light leading-none tracking-wide", "max-lg:text-4xl max-lg:leading-none max-lg:tracking-wider lg:!mb-8", "t2-dark:text-white"].join(" ");
export const HeroLeadPanel = t0 => {
  const $ = _c(68);
  const {
    eyebrow,
    header,
    headerElement: t1,
    headerClassName,
    subheader,
    description,
    backgroundImage,
    presetTheme,
    buttons,
    isHeaderBig,
    headerSize,
    isWrapped,
    isSearchEnabled,
    searchPlaceholderText,
    searchFilter,
    searchIndex,
    overlayOption,
    heroStyle: t2,
    componentImagePriority: t3,
    isLCP,
    videoPresent,
    breadcrumbs,
    breadcrumbDarkMode: t4
  } = t0;
  const headerElement = t1 === undefined ? "h1" : t1;
  const heroStyle = t2 === undefined ? "lead" : t2;
  const componentImagePriority = t3 === undefined ? "low" : t3;
  const breadcrumbDarkMode = t4 === undefined ? false : t4;
  const isSimple = heroStyle === "simple";
  const wrapperClasses = videoPresent ? "relative h-full px-5 lg:px-20 m-auto max-w-[1440px]" : isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : "";
  const columnTw = isSimple ? SIMPLE_COLUMN_TW : backgroundImage ? LEAD_COLUMN_TW : LEAD_WIDE_COLUMN_TW;
  const headerClasses = headerSize ? headerSize === "large" ? layoutHeaderBigClasses : headerSize === "small" ? layoutHeaderClasses : isSimple ? SIMPLE_HEADER_TW : heroLeadHeaderClasses : isHeaderBig ? layoutHeaderBigClasses : isSimple ? SIMPLE_HEADER_TW : heroLeadHeaderClasses;
  const t5 = headerClassName ? `${headerClasses} ${headerClassName}` : headerClasses;
  const t6 = header || "";
  let t7;
  if ($[0] !== headerElement || $[1] !== t5 || $[2] !== t6) {
    t7 = renderHeaderConditionally(t5, t6, headerElement);
    $[0] = headerElement;
    $[1] = t5;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const finalHeader = t7;
  const t8 = presetTheme || "";
  let t9;
  if ($[4] !== t8) {
    t9 = colorSettingDetection(t8);
    $[4] = t8;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const detectedColor = t9;
  let t10;
  if ($[6] !== backgroundImage || $[7] !== detectedColor || $[8] !== isSimple || $[9] !== overlayOption) {
    t10 = () => {
      if (overlayOption === "dark") {
        if (isSimple) {
          return "absolute inset-0 z-[2] pointer-events-none max-md:bg-[#252a2ea6] md:bg-[linear-gradient(270deg,#252a2e33,#252a2ee6)]";
        }
        return "absolute inset-0 z-[2] pointer-events-none max-md:bg-[#252a2e80] md:bg-[linear-gradient(270deg,#252a2e00,#252a2ee6)]";
      } else {
        if (overlayOption === "light") {
          if (isSimple) {
            return "absolute inset-0 z-[2] pointer-events-none max-md:bg-[#ffffffe6] md:bg-[linear-gradient(270deg,#ffffff33,#ffffffe6)]";
          }
          return "absolute inset-0 z-[2] pointer-events-none max-md:bg-[#ffffffe6] md:bg-[linear-gradient(270deg,#fff0,#ffffffe6)]";
        } else {
          if (backgroundImage) {
            if (detectedColor === "t2-color-setting--dark") {
              return "absolute inset-0 z-[2] pointer-events-none max-md:bg-[#252a2e80] md:hidden";
            } else {
              return "absolute inset-0 z-[2] pointer-events-none max-md:bg-[#ffffffe6] md:hidden";
            }
          }
          return null;
        }
      }
    };
    $[6] = backgroundImage;
    $[7] = detectedColor;
    $[8] = isSimple;
    $[9] = overlayOption;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const generateOverlayClass = t10;
  const contentSectionTheme = overlayOption === "dark" ? "t2-color-setting--dark" : detectedColor;
  const contentSectionThemeBck = backgroundImage ? "bg-transparent" : "";
  const t11 = presetTheme || "";
  let t12;
  if ($[11] !== t11) {
    t12 = themeTextureBackgroundImage(t11);
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const themeTextureSrc = t12;
  const displayTextureBackground = heroStyle === "simple" && !backgroundImage && themeTextureSrc;
  const overlayClass = generateOverlayClass();
  const hasBreadcrumbs = !!breadcrumbs?.length;
  const paddingClasses = videoPresent ? "flex flex-row items-center" : isSimple ? hasBreadcrumbs ? "pt-6 pb-24 min-[600px]:pt-10 min-[600px]:pb-[164px]" : "pt-20 pb-24 min-[600px]:pt-[120px] min-[600px]:pb-[164px]" : hasBreadcrumbs ? "pt-6 pb-24 min-[960px]:pt-10 min-[960px]:pb-[120px]" : "pt-20 pb-24 min-[960px]:pt-[120px] min-[960px]:pb-[120px]";
  const breadcrumbSpacing = videoPresent ? "mb-[26px]" : isSimple ? "mb-9 min-[600px]:mb-[60px]" : "mb-9 min-[960px]:mb-[60px]";
  const panelBgTheme = videoPresent ? "bg-transparent" : presetTheme || "";
  const t13 = `relative overflow-hidden ${paddingClasses} h-full ${panelBgTheme} ${contentSectionThemeBck} ${contentSectionTheme}`;
  let t14;
  if ($[13] !== displayTextureBackground || $[14] !== isLCP || $[15] !== themeTextureSrc || $[16] !== videoPresent) {
    t14 = displayTextureBackground && !videoPresent && <div className="absolute inset-0 z-[1] block w-full h-full"><Image src={themeTextureSrc} alt="" fill={true} className="w-full h-full object-cover object-left" quality={IMAGE_QUALITY_DESKTOP} sizes="(max-width: 600px) 600px, (max-width: 1200px) 1200px, 100%" priority={isLCP} /></div>;
    $[13] = displayTextureBackground;
    $[14] = isLCP;
    $[15] = themeTextureSrc;
    $[16] = videoPresent;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== backgroundImage || $[19] !== componentImagePriority || $[20] !== isLCP || $[21] !== videoPresent) {
    t15 = backgroundImage && !videoPresent && <HeroBackgroundImage image={backgroundImage} priority={componentImagePriority} isLCP={isLCP} />;
    $[18] = backgroundImage;
    $[19] = componentImagePriority;
    $[20] = isLCP;
    $[21] = videoPresent;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== overlayClass || $[24] !== videoPresent) {
    t16 = overlayClass && !videoPresent && <div className={overlayClass} data-testid="hero-overlay" />;
    $[23] = overlayClass;
    $[24] = videoPresent;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== breadcrumbDarkMode || $[27] !== breadcrumbSpacing || $[28] !== breadcrumbs || $[29] !== contentSectionTheme || $[30] !== hasBreadcrumbs || $[31] !== wrapperClasses) {
    t17 = hasBreadcrumbs && breadcrumbs && <Breadcrumbs items={breadcrumbs} darkMode={breadcrumbDarkMode && contentSectionTheme === "t2-color-setting--dark"} className={`${breadcrumbSpacing} ${wrapperClasses ? "" : "w-full px-5 lg:px-20 m-auto max-w-[1440px]"}`} />;
    $[26] = breadcrumbDarkMode;
    $[27] = breadcrumbSpacing;
    $[28] = breadcrumbs;
    $[29] = contentSectionTheme;
    $[30] = hasBreadcrumbs;
    $[31] = wrapperClasses;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== eyebrow || $[34] !== isSimple) {
    t18 = eyebrow && <div className={`t2-layout__eyebrow t2-hero__eyebrow ${isSimple ? "t2-dark:text-yellow-m10" : ""}`}>{eyebrow}</div>;
    $[33] = eyebrow;
    $[34] = isSimple;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== subheader) {
    t19 = subheader && <div className={layoutSubheaderClasses}>{subheader}</div>;
    $[36] = subheader;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== finalHeader || $[39] !== t18 || $[40] !== t19) {
    t20 = <div className="space-y-2.5 mb-[30px] min-[960px]:mb-[40px] min-[1231px]:mb-[60px]">{t18}{finalHeader}{t19}</div>;
    $[38] = finalHeader;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = `text-lg t2-layout__description ${isSimple ? "min-lg:max-w-[650px]" : ""}`;
  let t22;
  if ($[42] !== description || $[43] !== t21) {
    t22 = <CommonDescription description={description} className={t21} />;
    $[42] = description;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== isSearchEnabled || $[46] !== searchFilter || $[47] !== searchIndex || $[48] !== searchPlaceholderText) {
    t23 = isSearchEnabled && <div className="mt-[30px] min-[960px]:mt-[40px] min-[1231px]:mt-[60px]"><Suspense fallback={<div className="h-12" />}><HeroSearchButton label={searchPlaceholderText} searchFilter={searchFilter} searchIndex={searchIndex} /></Suspense></div>;
    $[45] = isSearchEnabled;
    $[46] = searchFilter;
    $[47] = searchIndex;
    $[48] = searchPlaceholderText;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== buttons) {
    t24 = buttons && <div className="flex flex-col md:flex-row gap-y-7 md:gap-x-4 md:gap-y-4 mt-[30px] min-[960px]:mt-[40px] min-[1231px]:mt-[60px]">{buttons.map(_temp)}</div>;
    $[50] = buttons;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== columnTw || $[53] !== t20 || $[54] !== t22 || $[55] !== t23 || $[56] !== t24) {
    t25 = <div className="flex flex-row flex-wrap mx-[-10px] flex-[0_1_auto]"><div className={columnTw}>{t20}{t22}{t23}{t24}</div></div>;
    $[52] = columnTw;
    $[53] = t20;
    $[54] = t22;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t17 || $[59] !== t25 || $[60] !== wrapperClasses) {
    t26 = <div className="relative z-[3] w-full"><div className={wrapperClasses}>{t17}{t25}</div></div>;
    $[58] = t17;
    $[59] = t25;
    $[60] = wrapperClasses;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t13 || $[63] !== t14 || $[64] !== t15 || $[65] !== t16 || $[66] !== t26) {
    t27 = <div className={t13}>{t14}{t15}{t16}{t26}</div>;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  return t27;
};
function _temp(btn, index) {
  return <TerraLink key={`cta-${btn?.label}-${index}`} {...btn} />;
}